import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LiffProvider } from './providers/LiffProvider';
import { useMobile } from './hooks/useMobile';
import { useMobileViewportFix } from './hooks/useMobileViewportFix';
import HomePage from './pages/HomePage';
import AdsListPage from './pages/advertising/AdsListPage';
import AdDetailPage from './pages/advertising/AdDetailPage';
import AdFormPage from './pages/advertising/AdFormPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import VerifyEmailLinkPage from './pages/auth/VerifyEmailLinkPage';
import VerifyEmailBindPage from './pages/auth/VerifyEmailBindPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import UnsubscribePage from './pages/UnsubscribePage';
import WishListPage from './pages/wishes/WishListPage';
import WishDetailPage from './pages/wishes/WishDetailPage';
import WishFormPage from './pages/wishes/WishFormPage';
import MobileHomePage from './pages/mobile/MobileHomePage';
import MobileAdsListPage from './pages/mobile/MobileAdsListPage';
import MobileAdDetailPage from './pages/mobile/MobileAdDetailPage';
import MobileAdFormPage from './pages/mobile/MobileAdFormPage';
import MobileLoginPage from './pages/mobile/MobileLoginPage';
import MobileRegisterPage from './pages/mobile/MobileRegisterPage';
import MobileWishListPage from './pages/mobile/MobileWishListPage';
import MobileWishDetailPage from './pages/mobile/MobileWishDetailPage';
import MobileWishFormPage from './pages/mobile/MobileWishFormPage';
import HelpPage from './pages/help/HelpPage';
import MobileHelpPage from './pages/mobile/MobileHelpPage';
import MemberDashboardPage from './pages/member/MemberDashboardPage';
import MobileMemberDashboardPage from './pages/mobile/MobileMemberDashboardPage';
import MemberAnalyticsPage from './pages/member/MemberAnalyticsPage';
import MemberSettingsPage from './pages/member/MemberSettingsPage';
import PricingPage from './pages/pricing/PricingPage';
import AdminDashboardPage from './pages/admin/dashboard';
import ContentManagementPage from './pages/admin/content';
import AdminQueuePage from './pages/admin/queue';
import AdminMembersPage from './pages/admin/members';
import MarqueeAdminPage from './pages/admin/MarqueeAdminPage';
import SystemBootstrapPage from './pages/admin/system/bootstrap';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import AdminRoute from './components/auth/AdminRoute';
import LiffApp from './components/line/LiffApp';
import LiffHomePage from './pages/line/LiffHomePage';
import NotFoundPage from './pages/NotFoundPage';
import MobileNotFoundPage from './pages/mobile/MobileNotFoundPage';
import './App.css';

// Wrapper component to handle dynamic device-based routing
const DeviceAwareRoute: React.FC<{
  desktopComponent: React.ComponentType;
  mobileComponent: React.ComponentType;
}> = ({ desktopComponent: DesktopComponent, mobileComponent: MobileComponent }) => {
  const { isPhone, isTablet } = useMobile();
  const shouldUseMobileView = isPhone || isTablet;
  const Component = shouldUseMobileView ? MobileComponent : DesktopComponent;
  return <Component />;
};

function App() {
  // Fix mobile viewport issues - applies to all mobile pages
  useMobileViewportFix();

  return (
    <LiffProvider>
      <AuthProvider>
        <Router>
          <Routes>
          {/* Routes with dynamic device detection */}
          <Route
            path="/"
            element={<DeviceAwareRoute desktopComponent={HomePage} mobileComponent={MobileHomePage} />}
          />
          <Route
            path="/ads"
            element={<DeviceAwareRoute desktopComponent={AdsListPage} mobileComponent={MobileAdsListPage} />}
          />
          <Route
            path="/ads/:adId"
            element={<DeviceAwareRoute desktopComponent={AdDetailPage} mobileComponent={MobileAdDetailPage} />}
          />
          <Route
            path="/ads/new"
            element={
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={AdFormPage} mobileComponent={MobileAdFormPage} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ads/:adId/edit"
            element={
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={AdFormPage} mobileComponent={MobileAdFormPage} />
              </ProtectedRoute>
            }
          />

          {/* Authentication Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <DeviceAwareRoute desktopComponent={LoginPage} mobileComponent={MobileLoginPage} />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <DeviceAwareRoute desktopComponent={RegisterPage} mobileComponent={MobileRegisterPage} />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={<ForgotPasswordPage />}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/verify-email"
            element={<EmailVerificationPage />}
          />
          <Route
            path="/verify-email/link"
            element={<VerifyEmailLinkPage />}
          />
          <Route
            path="/verify-email/bind"
            element={<VerifyEmailBindPage />}
          />
          <Route
            path="/unsubscribe"
            element={<UnsubscribePage />}
          />

          {/* Wishes Routes */}
          <Route
            path="/wishes"
            element={<DeviceAwareRoute desktopComponent={WishListPage} mobileComponent={MobileWishListPage} />}
          />
          <Route
            path="/wishes/:id"
            element={<DeviceAwareRoute desktopComponent={WishDetailPage} mobileComponent={MobileWishDetailPage} />}
          />
          <Route
            path="/wishes/new"
            element={
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={WishFormPage} mobileComponent={MobileWishFormPage} />
              </ProtectedRoute>
            }
          />

          {/* Member Routes */}
          <Route
            path="/member"
            element={
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={MemberDashboardPage} mobileComponent={MobileMemberDashboardPage} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/analytics"
            element={
              <ProtectedRoute>
                <MemberAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/settings"
            element={
              <ProtectedRoute>
                <MemberSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Pricing Route */}
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <PricingPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/content"
            element={
              <AdminRoute>
                <ContentManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/queue"
            element={
              <AdminRoute>
                <AdminQueuePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/marquees"
            element={
              <AdminRoute>
                <MarqueeAdminPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members"
            element={
              <AdminRoute>
                <AdminMembersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/system/bootstrap"
            element={
              <AdminRoute allowedRoles={['super_admin']}>
                <SystemBootstrapPage />
              </AdminRoute>
            }
          />

          {/* LIFF Routes */}
          <Route path="/liff" element={<LiffApp />}>
            <Route index element={<LiffHomePage />} />
            <Route path="home" element={<LiffHomePage />} />
            {/* Add more LIFF routes as needed */}
            {/* <Route path="ads" element={<LiffAdsListPage />} /> */}
            {/* <Route path="wishes" element={<LiffWishListPage />} /> */}
          </Route>

          {/* Help Routes */}
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={HelpPage} mobileComponent={MobileHelpPage} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/:manualId"
            element={
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={HelpPage} mobileComponent={MobileHelpPage} />
              </ProtectedRoute>
            }
          />

          {/* 404 Not Found - Must be last route */}
          <Route
            path="*"
            element={<DeviceAwareRoute desktopComponent={NotFoundPage} mobileComponent={MobileNotFoundPage} />}
          />
        </Routes>
      </Router>
    </AuthProvider>
  </LiffProvider>
  );
}

export default App;
